import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  Map, FolderOpen, Zap, LogOut, ChevronLeft, ChevronRight,
  X, Layers, Activity, Palette, MapPin, ShoppingCart,
} from 'lucide-react';
import { useAuth, useAuthStore } from '../../store/useAuthStore';
import { useTheme, THEMES } from '../../contexts/ThemeContext';
import { useCart } from '../../contexts/CartContext';
import LogoWordmark from '../LogoWordmark';
import './Sidebar.css';

function SbThemeSwitcher({ collapsed }) {
  const { themeId, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const current = THEMES[themeId];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="sb-link"
        onClick={() => setOpen(v => !v)}
        title="Thème"
        style={{ color: open ? 'var(--cyan-400, #22d3ee)' : undefined }}
      >
        <span className="sb-link-icon-wrap" style={{ background: open ? 'rgba(6,182,212,0.12)' : undefined }}>
          <Palette size={15} className="sb-link-icon" />
        </span>
        {!collapsed && (
          <>
            <span className="sb-link-label" style={{ flex: 1 }}>Thème</span>
            <span style={{
              width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
              background: current?.swatch, boxShadow: `0 0 5px ${current?.swatch}`,
            }} />
          </>
        )}
        {collapsed && <span className="sb-tooltip">Thème</span>}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: collapsed ? 'calc(100% + 10px)' : 0,
          marginBottom: collapsed ? undefined : 6,
          zIndex: 9999,
          background: 'rgba(10,16,30,0.98)',
          border: '1px solid rgba(6,182,212,0.22)',
          borderRadius: 10,
          padding: 6,
          minWidth: 152,
          boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
        }}>
          {Object.values(THEMES).map(t => (
            <button
              key={t.id}
              onClick={() => { setTheme(t.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                width: '100%', padding: '7px 10px', borderRadius: 6,
                border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                background: themeId === t.id ? 'rgba(6,182,212,0.12)' : 'transparent',
                color: themeId === t.id ? 'var(--cyan-400, #22d3ee)' : '#94a3b8',
                fontFamily: 'inherit', transition: 'background .12s',
              }}
              onMouseEnter={e => { if (themeId !== t.id) e.currentTarget.style.background = 'rgba(6,182,212,0.06)'; }}
              onMouseLeave={e => { if (themeId !== t.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
                background: t.swatch,
                boxShadow: themeId === t.id ? `0 0 6px ${t.swatch}` : 'none',
              }} />
              {t.label}
              {themeId === t.id && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--cyan-400, #22d3ee)' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ name, size = 32 }) {
  const initials = (name || 'U')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="sb-avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, collapsed, badge }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `sb-link${isActive ? ' sb-link--active' : ''}`}
    >
      <span className="sb-link-icon-wrap" style={{ position: 'relative' }}>
        <Icon size={16} className="sb-link-icon" />
        {badge > 0 && collapsed && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#4f46e5', color: '#fff',
            borderRadius: '50%', width: 14, height: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700,
          }}>{badge > 9 ? '9+' : badge}</span>
        )}
      </span>
      {!collapsed && <span className="sb-link-label">{label}</span>}
      {!collapsed && badge > 0 && (
        <span style={{
          marginLeft: 'auto', background: '#4f46e5', color: '#fff',
          borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 700,
        }}>{badge > 9 ? '9+' : badge}</span>
      )}
      {collapsed && (
        <span className="sb-tooltip">{label}{badge > 0 ? ` (${badge})` : ''}</span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAuth();
  const { items: cartItems } = useCart();
  const { themeId } = useTheme();

  const displayName = user?.username || user?.email?.split('@')[0] || 'Utilisateur';
  const displayRole = user?.role || 'Expert Géodésie';

  const handleLogout = () => {
    useAuthStore.logout();
    navigate('/login');
  };

  const handleNavClick = () => onMobileClose?.();

  return (
    <>
      {mobileOpen && (
        <div className="sb-overlay" onClick={onMobileClose} />
      )}

      <aside className={[
        'sb',
        collapsed   ? 'sb--collapsed'    : '',
        mobileOpen  ? 'sb--mobile-open'  : '',
      ].filter(Boolean).join(' ')}>

        {/* ── Scan line décoratif ── */}
        <div className="sb-scanline" aria-hidden />

        {/* ── Brand ── */}
        <Link to="/" className="sb-brand" onClick={handleNavClick}>
          <span className="sb-brand-icon">
            <Zap size={15} />
          </span>
          {!collapsed && (
            <span className="sb-brand-name">
              <LogoWordmark height={19} madColor={themeId === 'light' ? '#0f172a' : '#f0f6ff'} />
              <span className="sb-brand-tag">GIS</span>
              <span className="badge-beta">BETA</span>
            </span>
          )}
          <button
            className="sb-mobile-close"
            onClick={e => { e.preventDefault(); onMobileClose?.(); }}
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </Link>

        {/* ── User profile ── */}
        <div className="sb-user" onClick={handleNavClick}>
          <Avatar name={displayName} size={collapsed ? 30 : 34} />
          {!collapsed && (
            <div className="sb-user-info">
              <span className="sb-user-name">{displayName}</span>
              <span className="sb-user-role">{displayRole}</span>
            </div>
          )}
          {!collapsed && <span className="sb-online-dot" title="En ligne" />}
          {collapsed && <span className="sb-tooltip">{displayName}</span>}
        </div>

        {/* ── Navigation ── */}
        <nav className="sb-nav" onClick={handleNavClick}>
          <div className="sb-group">
            {!collapsed && <div className="sb-group-label">Modules</div>}
            <NavItem to="/expert/maps"         icon={Map}          label="Carte interactive" collapsed={collapsed} />
            <NavItem to="/expert/mes-points"   icon={MapPin}       label="Mes Points"        collapsed={collapsed} />
            <NavItem to="/expert/userFile"     icon={FolderOpen}   label="Gestion fichiers"  collapsed={collapsed} />
            <NavItem to="/expert/transLaborde" icon={Activity}     label="Transformation"    collapsed={collapsed} />
            <NavItem to="/cad"                 icon={Layers}       label="Viewer DXF"    collapsed={collapsed} />
          </div>
          <div className="sb-group">
            {!collapsed && <div className="sb-group-label">Achats</div>}
            <NavItem to="/expert/panier"       icon={ShoppingCart} label="Panier"            collapsed={collapsed} badge={cartItems.length} />
          </div>
        </nav>

        {/* ── Spacer ── */}
        <div className="sb-spacer" />

        {/* ── Bottom ── */}
        <div className="sb-bottom">
          <SbThemeSwitcher collapsed={collapsed} />

          <button
            className="sb-link sb-logout"
            onClick={handleLogout}
          >
            <span className="sb-link-icon-wrap">
              <LogOut size={15} className="sb-link-icon" />
            </span>
            {!collapsed && <span className="sb-link-label">Déconnexion</span>}
            {collapsed && <span className="sb-tooltip">Déconnexion</span>}
          </button>

          <button
            className="sb-collapse-btn"
            onClick={() => setCollapsed(v => !v)}
            aria-label={collapsed ? 'Déplier' : 'Réduire'}
            title={collapsed ? 'Déplier' : 'Réduire'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            {!collapsed && <span>Réduire</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
